import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { NativeBaseProvider } from 'native-base';
import React, { PropsWithChildren, useEffect } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

import { fonts } from '~/components/text';
import { AppConfigProvider } from '~/contexts/app-config';
import { AppUpdateProvider } from '~/contexts/app-update';
import { AuthProvider } from '~/contexts/auth';
import { AppIntlProvider } from '~/contexts/intl';
import { ModalAccessibilityProvider, useModalAccessibility } from '~/contexts/modal-accessibility';
import { NetworkServiceStateProvider } from '~/contexts/network-service-state';
import { ServiceNoticeProvider } from '~/contexts/service-notice';
import { UserIdentityProvider } from '~/contexts/user-identity';
import { withErrorDiagnostics } from '~/error';
import { AppNavigator } from '~/navigator/app-navigator';

import { nativeBaseTheme } from './native-base-theme';
import { ServiceNoticeGate } from './service-notice-gate';

SplashScreen.preventAutoHideAsync();

function ModalAccessibilityBackground(props: PropsWithChildren<object>) {
    const { children } = props;
    const { isModalOpen } = useModalAccessibility();

    return (
        <View
            style={{ flex: 1 }}
            accessibilityElementsHidden={Platform.OS === 'ios' && isModalOpen}
            importantForAccessibility={Platform.OS === 'android' && isModalOpen ? 'no-hide-descendants' : 'auto'}
        >
            {children}
        </View>
    );
}

export const App = withErrorDiagnostics(() => {
    const [fontsLoaded] = useFonts(fonts);

    useEffect(() => {
        if (fontsLoaded) {
            setTimeout(SplashScreen.hideAsync, 200);
        }
    }, [fontsLoaded]);

    return !fontsLoaded ? null : (
        <GestureHandlerRootView style={StyleSheet.absoluteFill}>
            <StatusBar style="auto" />

            <NativeBaseProvider theme={nativeBaseTheme}>
                <AppConfigProvider>
                    <AppIntlProvider>
                        <ServiceNoticeProvider>
                            <ServiceNoticeGate>
                                <UserIdentityProvider>
                                    <AuthProvider>
                                        <NetworkServiceStateProvider>
                                            <ModalAccessibilityProvider>
                                                <AppUpdateProvider>
                                                    <ModalAccessibilityBackground>
                                                        <AppNavigator />
                                                    </ModalAccessibilityBackground>
                                                </AppUpdateProvider>
                                            </ModalAccessibilityProvider>
                                        </NetworkServiceStateProvider>
                                    </AuthProvider>
                                </UserIdentityProvider>
                            </ServiceNoticeGate>
                        </ServiceNoticeProvider>
                    </AppIntlProvider>
                </AppConfigProvider>
            </NativeBaseProvider>
        </GestureHandlerRootView>
    );
});
